import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import Cookies from 'js-cookie';
import backendService from './backendservices/BackendService';

interface AnalyticsFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError?: (message: string) => void;
}

interface AnalyticsFormData {
  date: string;
  connectionsSent: number;
  emailsSent: number;
  connectionsAccepted: number;
  twitterDMsSent: number;
  telegramDMsSent: number;
  leadsGenerated: number;
  existingConnections: number;
  invertedConnections: number;
  notes: string;
}

export default function AnalyticsForm({ open, onClose, onSuccess, onError }: AnalyticsFormProps) {
  const [formData, setFormData] = useState<AnalyticsFormData>({
    date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    connectionsSent: 0,
    emailsSent: 0,
    connectionsAccepted: 0,
    twitterDMsSent: 0,
    telegramDMsSent: 0,
    leadsGenerated: 0,
    existingConnections: 0,
    invertedConnections: 0,
    notes: '',
  });

  // Separate state for display values (strings)
  const [displayValues, setDisplayValues] = useState({
    connectionsSent: '',
    emailsSent: '',
    connectionsAccepted: '',
    twitterDMsSent: '',
    telegramDMsSent: '',
    leadsGenerated: '',
    existingConnections: '',
    invertedConnections: '',
  });

  const [loading, setLoading] = useState(false);

  // Prevent minus key and other invalid keys
  const handleKeyDown =
    (field: keyof AnalyticsFormData) => (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (field === 'date' || field === 'notes') {
        return;
      }

      // Prevent minus, plus, and 'e' (scientific notation) keys
      if (
        event.key === '-' ||
        event.key === '+' ||
        event.key === 'e' ||
        event.key === 'E' ||
        event.key === '.'
      ) {
        event.preventDefault();
      }
    };

  const handleInputChange =
    (field: keyof AnalyticsFormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;

      if (field === 'date' || field === 'notes') {
        setFormData((prev) => ({
          ...prev,
          [field]: value,
        }));
      } else {
        // Only allow positive numbers or empty string - filter out negative signs and non-numeric characters
        let filteredValue = value;

        // Remove minus signs and any non-numeric characters (except empty string)
        if (value !== '') {
          // Remove all non-digit characters (this will remove minus, plus, decimals, etc.)
          filteredValue = value.replace(/[^0-9]/g, '');
        }

        // Only update display value while typing - don't parse yet
        setDisplayValues((prev) => ({
          ...prev,
          [field]: filteredValue,
        }));
      }
    };

  // Handle blur to finalize the number value
  const handleBlur = (field: keyof AnalyticsFormData) => () => {
    if (field === 'date' || field === 'notes') {
      return;
    }

    const value = displayValues[field as keyof typeof displayValues];

    // Parse and update form data when field loses focus
    if (value === '') {
      setFormData((prev) => ({
        ...prev,
        [field]: 0,
      }));
      setDisplayValues((prev) => ({
        ...prev,
        [field]: '',
      }));
    } else {
      const numValue = Number(value);
      if (!isNaN(numValue) && isFinite(numValue) && numValue >= 0) {
        const finalValue = Math.floor(numValue); // Already non-negative, just floor it
        setFormData((prev) => ({
          ...prev,
          [field]: finalValue,
        }));
        // Update display to show the final parsed value
        setDisplayValues((prev) => ({
          ...prev,
          [field]: finalValue.toString(),
        }));
      } else {
        // Invalid input, reset to empty
        setFormData((prev) => ({
          ...prev,
          [field]: 0,
        }));
        setDisplayValues((prev) => ({
          ...prev,
          [field]: '',
        }));
      }
    }
  };

  const handleSubmit = async () => {
    // Validate required date field
    if (!formData.date) {
      if (onError) {
        onError('Date is required');
      }
      return;
    }

    // Sync displayValues to formData before submission (in case user submitted without blurring fields)
    // This ensures we're submitting the latest values
    const numericFields: Array<
      keyof Pick<
        AnalyticsFormData,
        | 'connectionsSent'
        | 'emailsSent'
        | 'connectionsAccepted'
        | 'twitterDMsSent'
        | 'telegramDMsSent'
        | 'leadsGenerated'
        | 'existingConnections'
        | 'invertedConnections'
      >
    > = [
      'connectionsSent',
      'emailsSent',
      'connectionsAccepted',
      'twitterDMsSent',
      'telegramDMsSent',
      'leadsGenerated',
      'existingConnections',
      'invertedConnections',
    ];

    const syncedFormData: AnalyticsFormData = { ...formData };
    for (const field of numericFields) {
      const displayValue = displayValues[field as keyof typeof displayValues];
      if (displayValue !== '') {
        const numValue = Number(displayValue);
        if (!isNaN(numValue) && isFinite(numValue) && numValue >= 0) {
          (syncedFormData[field] as number) = Math.floor(numValue);
        }
      }
    }

    // Validate that all numeric fields are non-negative integers
    const hasNegativeValue = numericFields.some((field) => {
      const value = syncedFormData[field] as number;
      return value < 0;
    });
    if (hasNegativeValue) {
      if (onError) {
        onError('All numeric values must be non-negative numbers');
      }
      return;
    }

    setLoading(true);

    // Check if token exists
    const token = Cookies.get('token');
    if (!token) {
      if (onError) {
        onError('Authentication token not found. Please log in again.');
      }
      setLoading(false);
      return;
    }

    try {
      // Prepare the data to match backend interface using synced form data
      const submitData = {
        date: new Date(syncedFormData.date).toISOString(), // Convert to ISO string
        connectionsSent: syncedFormData.connectionsSent,
        emailsSent: syncedFormData.emailsSent,
        connectionsAccepted: syncedFormData.connectionsAccepted,
        twitterDMsSent: syncedFormData.twitterDMsSent,
        telegramDMsSent: syncedFormData.telegramDMsSent,
        leadsGenerated: syncedFormData.leadsGenerated,
        existingConnections: syncedFormData.existingConnections,
        invertedConnections: syncedFormData.invertedConnections,
        notes: syncedFormData.notes || undefined,
      };

      // The backend route is POST /api/v1/analytics
      const baseUrl = process.env.REACT_APP_BASE_URL;
      const analyticsEndpoint = `${baseUrl}/analytics`;

      console.log('Submitting analytics to:', analyticsEndpoint);
      const response = await backendService.Post(analyticsEndpoint, submitData);

      // Check if response is successful (status 200 or 201)
      if (response.status === 200 || response.status === 201) {
        console.log('Analytics form submitted successfully:', response.data);

        // Reset form
        const today = new Date().toISOString().split('T')[0];
        setFormData({
          date: today,
          connectionsSent: 0,
          emailsSent: 0,
          connectionsAccepted: 0,
          twitterDMsSent: 0,
          telegramDMsSent: 0,
          leadsGenerated: 0,
          existingConnections: 0,
          invertedConnections: 0,
          notes: '',
        });

        // Reset display values
        setDisplayValues({
          connectionsSent: '',
          emailsSent: '',
          connectionsAccepted: '',
          twitterDMsSent: '',
          telegramDMsSent: '',
          leadsGenerated: '',
          existingConnections: '',
          invertedConnections: '',
        });

        // Call success callback
        onSuccess();

        // Close the form
        onClose();
      } else {
        if (onError) {
          onError('Unexpected response from server');
        }
      }
    } catch (error: unknown) {
      console.error('Analytics form submission error:', error);
      let errorMessage = 'Failed to submit analytics data';

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string }; status?: number } };
        errorMessage = axiosError.response?.data?.message || errorMessage;

        // If it's an auth error, provide more helpful message
        if (
          axiosError.response?.status === 401 ||
          axiosError.response?.data?.message?.includes('Authorization') ||
          axiosError.response?.data?.message?.includes('token')
        ) {
          errorMessage =
            'Authentication failed. Please check if the endpoint path is correct. ' +
            'The backend route might be mounted at a different path (e.g., /api/v1/analytics). ' +
            'Original error: ' +
            (axiosError.response?.data?.message || errorMessage);
        }
      }

      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        date: today,
        connectionsSent: 0,
        emailsSent: 0,
        connectionsAccepted: 0,
        twitterDMsSent: 0,
        telegramDMsSent: 0,
        leadsGenerated: 0,
        existingConnections: 0,
        invertedConnections: 0,
        notes: '',
      });
      setDisplayValues({
        connectionsSent: '',
        emailsSent: '',
        connectionsAccepted: '',
        twitterDMsSent: '',
        telegramDMsSent: '',
        leadsGenerated: '',
        existingConnections: '',
        invertedConnections: '',
      });
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          background: '#0000',
          borderRadius: 3,
          border: '1px solid rgba(104, 113, 209, 0.15)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: '#000000',
          color: '#fff',
          py: 3,
          px: 3,
          borderRadius: '4px 4px 0 0',
          borderBottom: '2px solid rgba(104, 113, 209, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: '#000000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(104, 113, 209, 0.3)',
            }}
          >
            <Typography
              sx={{
                fontSize: '24px',
                lineHeight: 1,
              }}
            >
              📊
            </Typography>
          </Box>
          <Box>
            <Typography
              variant='h5'
              sx={{
                fontWeight: 700,
                letterSpacing: 0.5,
                mb: 0.5,
                color: '#fff',
              }}
            >
              Analytics Entry
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.875rem',
                fontWeight: 400,
              }}
            >
              Enter your daily metrics and achievements
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          background: '#000000',
          px: 3,
          py: 3,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(104, 113, 209, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(104, 113, 209, 0.5)',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 3 }}>
          <TextField
            label='Date'
            type='date'
            value={formData.date}
            onChange={handleInputChange('date')}
            fullWidth
            required
            InputLabelProps={{
              shrink: true,
            }}
            helperText='Select the date for this entry'
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(104, 113, 209, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#6871d1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#6871d1',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                color: '#aaa',
                '&.Mui-focused': {
                  color: '#6871d1',
                },
              },
              '& .MuiInputBase-input': {
                color: '#fff',
              },
              '& .MuiFormHelperText-root': {
                color: '#888',
              },
            }}
          />

          <TextField
            label='Number of Connections Sent'
            type='number'
            value={displayValues.connectionsSent}
            onChange={handleInputChange('connectionsSent')}
            onBlur={handleBlur('connectionsSent')}
            onKeyDown={handleKeyDown('connectionsSent')}
            fullWidth
            required
            inputProps={{ min: 0, step: 1 }}
            helperText='Number of LinkedIn connections sent today'
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(104, 113, 209, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#6871d1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#6871d1',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                color: '#aaa',
                '&.Mui-focused': {
                  color: '#6871d1',
                },
              },
              '& .MuiInputBase-input': {
                color: '#fff',
              },
              '& .MuiFormHelperText-root': {
                color: '#888',
              },
            }}
          />

          <TextField
            label='Number of Emails Sent'
            type='number'
            value={displayValues.emailsSent}
            onChange={handleInputChange('emailsSent')}
            onBlur={handleBlur('emailsSent')}
            onKeyDown={handleKeyDown('emailsSent')}
            fullWidth
            required
            inputProps={{ min: 0, step: 1 }}
            helperText='Number of emails sent today'
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(104, 113, 209, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#6871d1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#6871d1',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                color: '#aaa',
                '&.Mui-focused': {
                  color: '#6871d1',
                },
              },
              '& .MuiInputBase-input': {
                color: '#fff',
              },
              '& .MuiFormHelperText-root': {
                color: '#888',
              },
            }}
          />

          <TextField
            label='Number of Connections Accepted'
            type='number'
            value={displayValues.connectionsAccepted}
            onChange={handleInputChange('connectionsAccepted')}
            onBlur={handleBlur('connectionsAccepted')}
            onKeyDown={handleKeyDown('connectionsAccepted')}
            fullWidth
            required
            inputProps={{ min: 0, step: 1 }}
            helperText='Number of connections accepted today'
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(104, 113, 209, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#6871d1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#6871d1',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                color: '#aaa',
                '&.Mui-focused': {
                  color: '#6871d1',
                },
              },
              '& .MuiInputBase-input': {
                color: '#fff',
              },
              '& .MuiFormHelperText-root': {
                color: '#888',
              },
            }}
          />

          <TextField
            label='Number of DMs Sent on Twitter'
            type='number'
            value={displayValues.twitterDMsSent}
            onChange={handleInputChange('twitterDMsSent')}
            onBlur={handleBlur('twitterDMsSent')}
            onKeyDown={handleKeyDown('twitterDMsSent')}
            fullWidth
            required
            inputProps={{ min: 0, step: 1 }}
            helperText='Number of Twitter direct messages sent today'
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(104, 113, 209, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#6871d1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#6871d1',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                color: '#aaa',
                '&.Mui-focused': {
                  color: '#6871d1',
                },
              },
              '& .MuiInputBase-input': {
                color: '#fff',
              },
              '& .MuiFormHelperText-root': {
                color: '#888',
              },
            }}
          />

          <TextField
            label='Number of DMs Sent on Telegram'
            type='number'
            value={displayValues.telegramDMsSent}
            onChange={handleInputChange('telegramDMsSent')}
            onBlur={handleBlur('telegramDMsSent')}
            onKeyDown={handleKeyDown('telegramDMsSent')}
            fullWidth
            required
            inputProps={{ min: 0, step: 1 }}
            helperText='Number of Telegram direct messages sent today'
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(104, 113, 209, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#6871d1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#6871d1',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                color: '#aaa',
                '&.Mui-focused': {
                  color: '#6871d1',
                },
              },
              '& .MuiInputBase-input': {
                color: '#fff',
              },
              '& .MuiFormHelperText-root': {
                color: '#888',
              },
            }}
          />

          <TextField
            label='Number of Leads Generated'
            type='number'
            value={displayValues.leadsGenerated}
            onChange={handleInputChange('leadsGenerated')}
            onBlur={handleBlur('leadsGenerated')}
            onKeyDown={handleKeyDown('leadsGenerated')}
            fullWidth
            required
            inputProps={{ min: 0, step: 1 }}
            helperText='Number of leads generated today'
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(104, 113, 209, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#6871d1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#6871d1',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                color: '#aaa',
                '&.Mui-focused': {
                  color: '#6871d1',
                },
              },
              '& .MuiInputBase-input': {
                color: '#fff',
              },
              '& .MuiFormHelperText-root': {
                color: '#888',
              },
            }}
          />

          <TextField
            label='Number of Inverted Connections'
            type='number'
            value={displayValues.invertedConnections}
            onChange={handleInputChange('invertedConnections')}
            onBlur={handleBlur('invertedConnections')}
            onKeyDown={handleKeyDown('invertedConnections')}
            fullWidth
            required
            inputProps={{ min: 0, step: 1 }}
            helperText='Number of inverted connections today'
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(104, 113, 209, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#6871d1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#6871d1',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                color: '#aaa',
                '&.Mui-focused': {
                  color: '#6871d1',
                },
              },
              '& .MuiInputBase-input': {
                color: '#fff',
              },
              '& .MuiFormHelperText-root': {
                color: '#888',
              },
            }}
          />

          <TextField
            label='Existing Connections'
            type='number'
            value={displayValues.existingConnections}
            onChange={handleInputChange('existingConnections')}
            onBlur={handleBlur('existingConnections')}
            onKeyDown={handleKeyDown('existingConnections')}
            fullWidth
            required
            inputProps={{ min: 0, step: 1 }}
            helperText='Total number of existing connections'
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(104, 113, 209, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#6871d1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#6871d1',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                color: '#aaa',
                '&.Mui-focused': {
                  color: '#6871d1',
                },
              },
              '& .MuiInputBase-input': {
                color: '#fff',
              },
              '& .MuiFormHelperText-root': {
                color: '#888',
              },
            }}
          />

          <TextField
            label='Notes (Optional)'
            multiline
            rows={3}
            value={formData.notes}
            onChange={handleInputChange('notes')}
            fullWidth
            helperText='Additional notes or comments (max 1000 characters)'
            inputProps={{ maxLength: 1000 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'rgba(104, 113, 209, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#6871d1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#6871d1',
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                color: '#aaa',
                '&.Mui-focused': {
                  color: '#6871d1',
                },
              },
              '& .MuiInputBase-input': {
                color: '#fff',
              },
              '& .MuiFormHelperText-root': {
                color: '#888',
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          background: '#000000',
          px: 3,
          py: 2.5,
          borderRadius: '0 0 3px 3px',
          borderTop: '1px solid rgba(90, 92, 117, 0.15)',
          gap: 2,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            color: '#aaa',
            fontWeight: 500,
            px: 3,
            py: 1.2,
            textTransform: 'none',
            fontSize: '0.9375rem',
            borderRadius: 2,
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#fff',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color='inherit' /> : null}
          sx={{
            background: '#6871d1',
            color: '#fff',
            fontWeight: 600,
            px: 4,
            py: 1.2,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(104, 113, 209, 0.3)',
            textTransform: 'none',
            fontSize: '0.9375rem',
            '&:hover': {
              background: '#5a63bb',
              boxShadow: '0 6px 16px rgba(104, 113, 209, 0.4)',
            },
            '&:disabled': {
              background: 'rgba(104, 113, 209, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
          }}
        >
          {loading ? 'Submitting...' : 'Submit Analytics'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
